import { connect } from 'react-redux';
import { useMemo, useCallback } from 'react';
import { Route, Routes, Link, useSearchParams } from 'react-router-dom';
import { Tabs, Tab } from '@mui/material';
import BuildingsLoader from '../loaders/BuildingsLoader';
import OrganizationsLoader from '../loaders/OrganizationsLoader';
import BuildingsTable from './BuildingsTable';
import EquipmentTable from './EquipmentTable';
import PointsTable from './PointsTable';
import Trends from './Trends';
import TooltipTab from '../util/TooltipTab';
import EquipTypeLoader from '../loaders/EquipTypeLoader';
import PointTypeLoader from '../loaders/PointTypeLoader';
import LabelsLoader from '../loaders/LabelsLoader';
import TagsLoader from '../loaders/TagsLoader';
import MeasurementsLoader from '../loaders/MeasurementsLoader';
import UnitsLoader from '../loaders/UnitsLoader';
import { useRouteMatch } from '../../utils/url';
import {
  tryParse,
  extractSortingParams,
  extractFilterParams,
  setSorting,
  setFilters,
} from '../util/DataTable';

function ExploreTabs({ searchParams, numSelectedBuildings, numSelectedPoints }) {
  // You need to provide the routes in descendant order.
  // This means that if you have nested routes like:
  // users, users/new, users/edit.
  // Then the order should be ['users/add', 'users/edit', 'users'].
  const routeMatch = useRouteMatch([
    '/explore/equipment',
    '/explore/points',
    '/explore/trends',
    '/',
  ]);
  const buildingsMessage = useMemo(() => {
    if (numSelectedBuildings === 0) return 'You must select at least 1 building before proceeding';
    if (numSelectedBuildings > 10)
      return 'You cannot select more than 10 buildings before proceeding';
    return null;
  }, [numSelectedBuildings]);
  const pointsMessage = useMemo(() => {
    if (numSelectedPoints === 0) return 'You must select at least 1 point before proceeding';
    if (numSelectedPoints > 100) return 'You cannot select more than 100 points before proceeding';
    return null;
  }, [numSelectedPoints]);

  const currentTab = routeMatch?.pattern?.path || '/';

  return (
    <Tabs textColor="secondary" indicatorColor="secondary" value={currentTab}>
      <Tab
        label="Buildings"
        value="/"
        to={`/explore/buildings?${searchParams.toString()}`}
        component={Link}
      />
      <TooltipTab
        value="/explore/equipment"
        message={buildingsMessage}
        label="Equipment"
        to={`/explore/equipment?${searchParams.toString()}`}
        component={Link}
      />
      <TooltipTab
        value="/explore/points"
        message={buildingsMessage}
        label="Points"
        to={`/explore/points?${searchParams.toString()}`}
        component={Link}
      />
      <TooltipTab
        value="/explore/trends"
        message={pointsMessage}
        label="Trends"
        to={`/explore/trends?${searchParams.toString()}`}
        component={Link}
      />
    </Tabs>
  );
}

const ExploreView = ({ buildingsById, orgsById }) => {
  // Snagged from https://github.com/TanStack/table/discussions/5002#discussioncomment-9778436
  const [searchParams, setSearchParams] = useSearchParams();
  const sorting = useMemo(() => extractSortingParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => extractFilterParams(searchParams), [searchParams]);

  const buildingsSelection = useMemo(
    () =>
      Object.fromEntries(tryParse(searchParams.get('bids')).map((bid) => [bid.toString(), true])),
    [searchParams]
  );
  const equipmentSelection = useMemo(
    () =>
      Object.fromEntries(tryParse(searchParams.get('eids')).map((eid) => [eid.toString(), true])),
    [searchParams]
  );
  const pointSelection = useMemo(
    () =>
      Object.fromEntries(tryParse(searchParams.get('pids')).map((pid) => [pid.toString(), true])),
    [searchParams]
  );

  const filteredBuildingsById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(buildingsSelection)
          // eslint-disable-next-line no-unused-vars
          .filter(([_k, v]) => v)
          // eslint-disable-next-line no-unused-vars
          .map(([bid, _v]) => buildingsById[bid])
          .filter(Boolean)
          .map((b) => [b.id.toString(), b])
      ),
    [buildingsSelection, buildingsById]
  );

  const setSortingCb = useCallback(
    (updater) => setSorting(updater, sorting, setSearchParams),
    [setSearchParams, sorting]
  );
  const setColumnFiltersCb = useCallback(
    (updater) => setFilters(updater, columnFilters, setSearchParams),
    [setSearchParams, columnFilters]
  );

  const setBuildingsSelection = useCallback(
    (updater) => {
      let newBuildingsSelection =
        typeof updater === 'function' ? updater(buildingsSelection) : updater;

      newBuildingsSelection = Object.entries(newBuildingsSelection)
        // eslint-disable-next-line no-unused-vars
        .filter(([_k, v]) => v)
        // eslint-disable-next-line no-unused-vars
        .map(([k, _v]) => parseInt(k, 10));
      if (newBuildingsSelection !== buildingsSelection) {
        if (newBuildingsSelection.length === 0)
          setSearchParams((params) => {
            params.delete('bids');
            return params;
          });
        else {
          setSearchParams((params) => {
            params.set('bids', JSON.stringify(newBuildingsSelection));
            return params;
          });
        }
      }
    },
    [setSearchParams, buildingsSelection]
  );

  const setEquipmentSelection = useCallback(
    (updater) => {
      let newEquipmentSelection =
        typeof updater === 'function' ? updater(equipmentSelection) : updater;

      newEquipmentSelection = Object.entries(newEquipmentSelection)
        // eslint-disable-next-line no-unused-vars
        .filter(([_k, v]) => v)
        // eslint-disable-next-line no-unused-vars
        .map(([k, _v]) => parseInt(k, 10));
      if (newEquipmentSelection !== equipmentSelection) {
        if (newEquipmentSelection.length === 0)
          setSearchParams((params) => {
            params.delete('eids');
            return params;
          });
        else {
          setSearchParams((params) => {
            params.set('eids', JSON.stringify(newEquipmentSelection));
            return params;
          });
        }
      }
    },
    [setSearchParams, equipmentSelection]
  );
  const setPointSelection = useCallback(
    (updater) => {
      let newPointSelection = typeof updater === 'function' ? updater(pointSelection) : updater;
      newPointSelection = Object.entries(newPointSelection)
        // eslint-disable-next-line no-unused-vars
        .filter(([_k, v]) => v)
        // eslint-disable-next-line no-unused-vars
        .map(([k, _v]) => parseInt(k, 10));
      if (newPointSelection !== pointSelection) {
        if (newPointSelection.length === 0)
          setSearchParams((params) => {
            params.delete('pids');
            return params;
          });
        else {
          setSearchParams((params) => {
            params.set('pids', JSON.stringify(newPointSelection));
            return params;
          });
        }
      }
    },
    [setSearchParams, pointSelection]
  );

  if (Object.keys(buildingsById).length <= 0)
    return (
      <>
        <OrganizationsLoader />
        <BuildingsLoader />
        <EquipTypeLoader />
        <PointTypeLoader />
        <LabelsLoader />
        <TagsLoader />
        <MeasurementsLoader />
      </>
    );
  return (
    <div>
      <OrganizationsLoader />
      <BuildingsLoader />
      <EquipTypeLoader />
      <PointTypeLoader />
      <LabelsLoader />
      <TagsLoader />
      <MeasurementsLoader />
      <UnitsLoader />
      <ExploreTabs
        searchParams={searchParams}
        numSelectedBuildings={Object.keys(filteredBuildingsById || {}).length}
        numSelectedPoints={Object.keys(pointSelection || {}).length}
      />
      <Routes>
        <Route
          path="/explore/equipment"
          element={
            <EquipmentTable
              sorting={sorting}
              onSortingChange={setSortingCb}
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFiltersCb}
              buildingsById={filteredBuildingsById}
              orgsById={orgsById}
              equipmentSelection={equipmentSelection}
              onRowSelectionChange={setEquipmentSelection}
            />
          }
        />
        <Route
          path="/explore/points"
          element={
            <PointsTable
              sorting={sorting}
              onSortingChange={setSortingCb}
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFiltersCb}
              buildingsById={filteredBuildingsById}
              orgsById={orgsById}
              equipmentSelection={equipmentSelection}
              pointSelection={pointSelection}
              onRowSelectionChange={setPointSelection}
            />
          }
        />
        <Route
          path="/explore/trends"
          element={
            <Trends
              buildingsById={filteredBuildingsById}
              pointIds={Object.entries(pointSelection)
                // eslint-disable-next-line no-unused-vars
                .filter(([_pid, include]) => include)
                .map(([pid]) => pid)}
            />
          }
        />
        <Route
          path="*"
          element={
            <BuildingsTable
              sorting={sorting}
              onSortingChange={setSortingCb}
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFiltersCb}
              buildingsById={buildingsById}
              orgsById={orgsById}
              buildingsSelection={buildingsSelection}
              onRowSelectionChange={setBuildingsSelection}
            />
          }
        />
      </Routes>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    entities: {
      buildings: { byId: buildingsById },
    },
    users: { orgsById },
  } = state;
  return {
    buildingsById,
    orgsById,
  };
};

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(ExploreView);
